/**
 * Milk Providers API Client
 *
 * Wraps the milkProviders, mother-infant-links, health-screening, and
 * designated-babies endpoints from tms-api-patient service.
 * Base URL is shared with the patient API (VITE_API_URL / config.api.baseUrl).
 */
import type { AxiosResponse } from 'axios';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  CreateMilkBottleLabelsRequest,
  CreateMilkBottleLabelsResponse,
  DonorDashboard,
} from '@tms-phoenix/tms-types';
import type {
  ApiMilkProviderSummary,
  ApiMilkProviderResponse,
  ApiMilkProviderListItem,
  ApiMilkProviderDetail,
  MilkProviderListQuery,
  ApiPagination,
  RegisterMilkProviderRequest,
  UpdateMilkProviderRequest,
  LinkPersonToPatientRequest,
  ApiMotherInfantLink,
  ApiGetMotherInfantLinksResponse,
  BulkLinkMotherInfantRequest,
  BulkLinkMotherInfantResponse,
  CreateHealthScreeningRequest,
  ApiHealthScreeningResponse,
  ApiHealthRecordGroup,
  UpdateHealthRecordRequest,
  ApiHealthRecord,
  ApiDesignatedBaby,
  DesignatedBabiesQuery,
  CreateDonorPreferenceRequest,
  ApiDonorPreferenceResponse,
  DocumentType,
  DocumentSubjectType,
  ApiDocumentUploadResponse,
  ApiDocumentBulkUploadResponse,
  BulkUploadFileMetadata,
  PersonDocumentsResponse,
  PatchSupportingDocumentResponse,
  PatchHealthRecordAttachmentResponse,
} from '@/modules/mothers/types/milk-provider.types';

const API_PREFIX = API_ROUTES.PATIENT;
const api = getApiClient();

// --- Milk Providers -- DD list ----------------------------------------------

/**
 * GET /api/v1/milkProviders/mothers
 *
 * Get all active milk providers where person_type is MOTHER or BOTH.
 * Returns a slim summary: { person_id, name }.
 * Used for dropdown / autocomplete lists.
 *
 * Note: The response interceptor unwraps the ApiResponse envelope,
 * so response.data is the inner payload directly.
 */
export function fetchMothers(hospitalId: string): Promise<AxiosResponse<ApiMilkProviderSummary[]>> {
  return api.get<ApiMilkProviderSummary[]>(
    `${API_PREFIX}/milkProviders/mothers`,
    { params: { hospital_id: hospitalId } },
  );
}

// --- Milk Providers -- paginated list --------------------------------------

/** Response shape of GET /milkProviders/list (data + pagination meta). */
export interface MilkProviderListResponse {
  items: ApiMilkProviderListItem[];
  pagination: ApiPagination | null;
}

/**
 * GET /api/v1/milkProviders/list
 *
 * Paginated list of all milk providers with optional role / search / sort filters.
 * Used for the mothers & donors listing view.
 *
 * The interceptor puts the ApiResponse.data array in response.data and the
 * pagination object is kept in response.headers or a custom field. Because the
 * interceptor unwraps data -> we get the items array directly in response.data.
 * We fetch meta separately by accessing the raw axios response object's axiosResponse
 * property set by the interceptor, or we accept that pagination is lost and handle
 * it differently.
 *
 * Implementation note: The interceptor sets response.data = apiResponse.data (the
 * items array) and drops meta. To preserve pagination, we read it via a custom
 * approach -- this function returns a combined object by peeking at the full response.
 *
 * Pragmatic approach: use the returned AxiosResponse<ApiMilkProviderListItem[]>
 * and expose pagination via a separate typed wrapper stored on the
 * call site (store). The interceptor only strips the ApiResponse wrapper; the
 * `meta` field is still available via the _axiosMeta custom property added by
 * the service-core interceptor (if present), otherwise we call the endpoint
 * directly with a custom transformer.
 *
 * Simplest correct approach: the interceptor from createApiClient sets
 * `response.data = apiResponse.data` (inner array) and also attaches
 * `response.pagination = apiResponse.meta.pagination` in the service-core
 * interceptor. We cast accordingly.
 */
export function fetchMilkProvidersList(
  query: MilkProviderListQuery,
): Promise<AxiosResponse<ApiMilkProviderListItem[]>> {
  return api.get<ApiMilkProviderListItem[]>(
    `${API_PREFIX}/milkProviders/list`,
    { params: query, paramsSerializer: { indexes: null } },
  );
}

// --- Milk Providers -- single profile --------------------------------------

/**
 * GET /api/v1/milkProviders/:person_id
 *
 * Return the full profile of a single milk provider including bed, unit,
 * facility, active exclusion, consent, and exclusion history.
 */
export function fetchMilkProviderById(
  personId: string,
): Promise<AxiosResponse<ApiMilkProviderDetail>> {
  return api.get<ApiMilkProviderDetail>(
    `${API_PREFIX}/milkProviders/${personId}`,
  );
}

// --- Milk Providers -- mutations --------------------------------------------

/**
 * POST /api/v1/milkProviders
 *
 * Register a new milk provider (MOTHER, DONOR, or BOTH).
 * Atomically creates milk_providers row, subject_pseudonyms,
 * optionally clinical_consent_records and milk_provider_exclusions.
 */
export function registerMilkProvider(body: RegisterMilkProviderRequest): Promise<AxiosResponse<ApiMilkProviderResponse>> {
  return api.post<ApiMilkProviderResponse>(
    `${API_PREFIX}/milkProviders`,
    body,
  );
}

/**
 * PATCH /api/v1/milkProviders/:person_id
 *
 * Partially update a milk provider. Only supplied fields are written.
 * hospital_id is always required.
 */
export function updateMilkProvider(personId: string, body: UpdateMilkProviderRequest): Promise<AxiosResponse<ApiMilkProviderResponse>> {
  return api.patch<ApiMilkProviderResponse>(
    `${API_PREFIX}/milkProviders/${personId}`,
    body,
  );
}

// --- Convert Mother to Donor ------------------------------------------------

import type { ConvertMotherToDonorRequest } from '@tms-phoenix/tms-types';

/**
 * POST /api/v1/milkProviders/convertToDonor
 *
 * Convert an existing Mother to Mother+Donor (BOTH).
 * Only mothers can be converted; donors cannot become mothers.
 * staff_id is optional per the backend schema.
 */
export function convertToDonor(
  body: ConvertMotherToDonorRequest,
): Promise<AxiosResponse<ApiMilkProviderResponse>> {
  return api.post<ApiMilkProviderResponse>(
    `${API_PREFIX}/milkProviders/convertToDonor`,
    body,
  );
}

// --- Discharge / Re-admit ----------------------------------------------------

import type {
  DischargeMilkProviderRequest,
  DischargeMilkProviderResponse,
} from '@tms-phoenix/tms-types';

/**
 * POST /api/v1/milkProviders/:person_id/discharge
 *
 * Discharge a milk provider from the MOTHER, DONOR, or BOTH role.
 * When person_type is BOTH, discharge_role must be supplied to select which
 * role(s) to discharge (defaults to BOTH / full discharge on the server).
 */
export function dischargeMilkProvider(
  personId: string,
  body: DischargeMilkProviderRequest,
): Promise<AxiosResponse<DischargeMilkProviderResponse>> {
  return api.post<DischargeMilkProviderResponse>(
    `${API_PREFIX}/milkProviders/${personId}/discharge`,
    body,
  );
}

import type {
  ReAdmitDonorRequest,
  ReAdmitDonorResponse,
} from '@tms-phoenix/tms-types';

/**
 * POST /api/v1/milkProviders/:person_id/readmit-donor
 *
 * Re-admit the DONOR role of a milk provider. Activates the DONOR role
 * only (person_type becomes BOTH when the MOTHER role is still active,
 * otherwise DONOR) and resets screening_status to PENDING.
 */
export function readmitDonor(
  personId: string,
  body: ReAdmitDonorRequest,
): Promise<AxiosResponse<ReAdmitDonorResponse>> {
  return api.post<ReAdmitDonorResponse>(
    `${API_PREFIX}/milkProviders/${personId}/readmit-donor`,
    body,
  );
}

// --- Mother--Infant Links ---------------------------------------------------

/**
 * POST /api/v1/motherInfantLinks/link
 *
 * Link a person (mother/donor) to a patient (infant).
 * Returns 409 if the link already exists.
 */
export function linkMotherToInfant(
  body: LinkPersonToPatientRequest,
): Promise<AxiosResponse<ApiMotherInfantLink>> {
  return api.post<ApiMotherInfantLink>(
    `${API_PREFIX}/motherInfantLinks/link`,
    body,
  );
}

/**
 * PATCH /api/v1/motherInfantLinks/:link_id
 *
 * Deactivate an existing person--patient link (sets effective_to = now()).
 */
export function deactivateMotherInfantLink(
  linkId: string,
): Promise<AxiosResponse<ApiMotherInfantLink>> {
  return api.patch<ApiMotherInfantLink>(
    `${API_PREFIX}/motherInfantLinks/${linkId}`,
  );
}

/**
 * POST /api/v1/motherInfantLinks/bulk-link
 *
 * Bulk-link a person (mother) to one or more patients (infants).
 * Used when the Mother role is selected during registration.
 */
export function bulkLinkMotherToInfants(
  body: BulkLinkMotherInfantRequest,
): Promise<AxiosResponse<BulkLinkMotherInfantResponse>> {
  return api.post<BulkLinkMotherInfantResponse>(
    `${API_PREFIX}/motherInfantLinks/bulk-link`,
    body,
  );
}

/**
 * GET /api/v1/motherInfantLinks
 *
 * Fetch mother--infant links enriched with related entity details.
 * Provide exactly one of patient_id or person_id.
 *
 * Returns a discriminated union based on which param was provided.
 */
export function fetchMotherInfantLinks(params: {
  hospital_id: string;
  patient_id?: string;
  person_id?: string;
}): Promise<AxiosResponse<ApiGetMotherInfantLinksResponse>> {
  return api.get<ApiGetMotherInfantLinksResponse>(
    `${API_PREFIX}/motherInfantLinks`,
    { params },
  );
}

// --- Test Types -------------------------------------------------------------

/** Shape of a single item returned by GET /test-types */
export interface ApiTestType {
  test_type: string;
}

/** Full response envelope from GET /test-types */
export interface ApiTestTypesResponse {
  success: boolean;
  data: ApiTestType[];
}

/**
 * GET /api/v1/milkProviderHealthRecords/test-types
 *
 * Returns the master list of serology / infectious-disease test types
 * configured in the system (e.g. HIV, HBsAg, HCV, HTLV, CMV, Syphilis).
 */
export function fetchTestTypes(): Promise<AxiosResponse<ApiTestTypesResponse>> {
  return api.get<ApiTestTypesResponse>(
    `${API_PREFIX}/milkProviderHealthRecords/test-types`,
  );
}

// --- Health Screening ------------------------------------------------------

/**
 * POST /api/v1/milkProviderHealthRecords/health-screening
 *
 * Submit a structured health-screening form for a milk provider.
 * At least one section (serology, health_history, lifestyle, obstetric_history,
 * or medication) must be provided.
 * Serology entries always create new rows; other sections are upserted.
 */
export function createHealthScreening(
  body: CreateHealthScreeningRequest,
): Promise<AxiosResponse<ApiHealthScreeningResponse>> {
  return api.post<ApiHealthScreeningResponse>(
    `${API_PREFIX}/milkProviderHealthRecords/health-screening`,
    body,
  );
}

/**
 * GET /api/v1/milkProviderHealthRecords/:person_id
 *
 * Returns all health records for a milk provider, grouped by admission cycle
 * (`person_sr_no`). The response is an array sorted so the current (ACTIVE)
 * admission is always at index 0; earlier admissions are PAST. Empty array
 * when the person has no health records.
 *
 * Note: the `record_type` filter previously supported here has been removed
 * by the API -- passing it now returns a 400 VALIDATION_ERROR.
 */
export function fetchHealthRecords(
  personId: string,
): Promise<AxiosResponse<ApiHealthRecordGroup[]>> {
  return api.get<ApiHealthRecordGroup[]>(
    `${API_PREFIX}/milkProviderHealthRecords/${personId}`,
  );
}

/**
 * PATCH /api/v1/milkProviderHealthRecords/:health_record_id
 *
 * Partially update an existing health record.
 * Only the fields provided in the request body are updated.
 */
export function updateHealthRecord(
  healthRecordId: string,
  body: UpdateHealthRecordRequest,
): Promise<AxiosResponse<ApiHealthRecord>> {
  return api.patch<ApiHealthRecord>(
    `${API_PREFIX}/milkProviderHealthRecords/${healthRecordId}`,
    body,
  );
}

/**
 * DELETE /api/v1/milkProviderHealthRecords/:health_record_id
 *
 * Soft-delete a health record (sets is_deleted = true).
 * The record is hidden from GET responses but not physically removed.
 * Returns 404 if already soft-deleted.
 */
export function deleteHealthRecord(
  healthRecordId: string,
): Promise<AxiosResponse<void>> {
  return api.delete<void>(
    `${API_PREFIX}/milkProviderHealthRecords/${healthRecordId}`,
  );
}

// --- Screening Approval / Rejection -----------------------------------------

/** Response from POST /:person_id/approve-screening */
export interface ApproveScreeningResponse {
  person_id: string;
  screening_status: 'APPROVED';
  approved_by: string;
  approved_at: string;
}

/** Response from POST /:person_id/reject-screening */
export interface RejectScreeningResponse {
  person_id: string;
  screening_status: 'REJECTED';
  rejection_reason?: string;
}

/**
 * POST /api/v1/milkProviderHealthRecords/:person_id/approve-screening
 *
 * Nurse approval endpoint. Transitions a donor whose screening_status is
 * CLEARED to APPROVED. Requires is_consent_given = true, no active exclusion,
 * and person_type = DONOR or BOTH.
 */
export function approveScreening(
  personId: string,
  approvedBy: string,
): Promise<AxiosResponse<ApproveScreeningResponse>> {
  return api.post<ApproveScreeningResponse>(
    `${API_PREFIX}/milkProviderHealthRecords/${personId}/approve-screening`,
    { approved_by: approvedBy },
  );
}

/**
 * POST /api/v1/milkProviderHealthRecords/:person_id/reject-screening
 *
 * Nurse rejection endpoint. Transitions a donor whose screening_status is
 * CLEARED or APPROVED to REJECTED. Optionally includes a reason.
 */
export function rejectScreening(
  personId: string,
  rejectedBy: string,
  rejectionReason?: string,
): Promise<AxiosResponse<RejectScreeningResponse>> {
  return api.post<RejectScreeningResponse>(
    `${API_PREFIX}/milkProviderHealthRecords/${personId}/reject-screening`,
    {
      rejected_by: rejectedBy,
      rejection_reason: rejectionReason || undefined,
    },
  );
}

// --- Designated Babies ------------------------------------------------------

/**
 * GET /api/v1/patientDonorPreferences/donors/:donor_id/patients
 *
 * Get all patients that have a preference assigned for a given donor,
 * ordered by priority_order ascending.
 * Includes patient demographics, current bed location, allergies, and
 * preference metadata.
 */
export function fetchDesignatedBabies(
  donorId: string,
  query?: DesignatedBabiesQuery,
): Promise<AxiosResponse<ApiDesignatedBaby[]>> {
  return api.get<ApiDesignatedBaby[]>(
    `${API_PREFIX}/patientDonorPreferences/donors/${donorId}/patients`,
    { params: query ?? {} },
  );
}

/**
 * POST /api/v1/patientDonorPreferences
 *
 * Create a new donor--patient preference (designate a recipient).
 * Assigns a patient as a designated recipient for a donor's milk.
 */
export function createDonorPreference(
  body: CreateDonorPreferenceRequest,
): Promise<AxiosResponse<ApiDonorPreferenceResponse>> {
  return api.post<ApiDonorPreferenceResponse>(
    `${API_PREFIX}/patientDonorPreferences`,
    body,
  );
}

/**
 * PATCH /api/v1/patientDonorPreferences/:preference_id
 *
 * Deactivate an existing donor--patient preference
 * (sets is_active = false, effective_to = now()).
 */
export function deactivateDonorPreference(
  preferenceId: string,
): Promise<AxiosResponse<ApiDonorPreferenceResponse>> {
  return api.patch<ApiDonorPreferenceResponse>(
    `${API_PREFIX}/patientDonorPreferences/${preferenceId}`,
  );
}

// --- Documents (S3) -------------------------------------------------------

/**
 * POST /api/v1/documents/:hospital_id/:subject_type/:subject_id?doc_type=...
 *
 * Upload a single document for a subject (milk-provider, patient, etc.).
 * The file is stored in S3 and a 15-minute pre-signed GET URL is returned.
 *
 * Uses multipart/form-data -- the Content-Type header is set automatically
 * by axios when a FormData body is provided.
 */
export function uploadDocument(
  hospitalId: string,
  subjectType: DocumentSubjectType,
  subjectId: string,
  file: File,
  docType: DocumentType,
): Promise<AxiosResponse<ApiDocumentUploadResponse>> {
  const formData = new FormData();
  formData.append('file', file);

  return api.post<ApiDocumentUploadResponse>(
    `${API_PREFIX}/documents/${hospitalId}/${subjectType}/${subjectId}`,
    formData,
    {
      params: { doc_type: docType },
    },
  );
}

/**
 * POST /api/v1/documents/bulk
 *
 * Upload multiple documents in a single request (up to 10 files, 10 MB total).
 * Each file is processed independently -- a failure on one does NOT abort the others.
 * Results are returned in the same order as received.
 *
 * hospital_id, subject_type, and subject_id are passed per-file inside the
 * metadata JSON array (not in the URL path).
 *
 * @param hospitalId   Hospital UUID — injected into each metadata entry.
 * @param subjectType  Subject type slug (e.g. 'milk-provider') — mapped to
 *                     uppercase ('DONOR'|'MOTHER'|'PATIENT') in each metadata entry.
 * @param subjectId    Subject UUID — injected into each metadata entry.
 * @param metadata     Per-file metadata array (one entry per file, same order).
 *                     Each entry must include at least `doc_type`.
 * @param staffId      Optional staff UUID -- overrides the JWT sub as uploaded_by.
 */
export function uploadDocumentsBulk(
  hospitalId: string,
  subjectType: DocumentSubjectType,
  subjectId: string,
  files: File[],
  metadata: BulkUploadFileMetadata[],
  staffId?: string,
): Promise<AxiosResponse<ApiDocumentBulkUploadResponse>> {
  // Map the slug to the uppercase enum the backend expects
  const subjectTypeMap: Record<DocumentSubjectType, string> = {
    'milk-provider': 'DONOR',
    'patient': 'PATIENT',
  };
  const mappedSubjectType = subjectTypeMap[subjectType] ?? subjectType;

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  formData.append('metadata', JSON.stringify(metadata));

  return api.post<ApiDocumentBulkUploadResponse>(
    `${API_PREFIX}/documents/bulk`,
    formData,
    {
      params: {
        hospital_id: hospitalId,
        subject_type: mappedSubjectType,
        subject_id: subjectId,
        ...(staffId ? { staff_id: staffId } : {}),
      },
    },
  );
}

/**
 * GET /api/v1/documents/:hospital_id/fetch?url=...
 *
 * Download a document from S3 using its permanent URL.
 * The file is streamed directly to the client with its original Content-Type header.
 * Returns the response as a Blob so it can be opened / saved by the browser.
 */
export function fetchDocumentByUrl(
  hospitalId: string,
  url: string,
): Promise<AxiosResponse<Blob>> {
  return api.get<Blob>(
    `${API_PREFIX}/documents/${hospitalId}/fetch`,
    {
      params: { url },
      responseType: 'blob',
    },
  );
}

/**
 * GET /api/v1/documents/:person_id
 *
 * Fetch all documents for a person (consent documents + health record attachments).
 * Returns consent_documents and attachments arrays.
 * Use the document types to display documents in the appropriate profile section.
 */
export function fetchDocumentsByPerson(
  personId: string,
): Promise<AxiosResponse<PersonDocumentsResponse>> {
  return api.get<PersonDocumentsResponse>(
    `${API_PREFIX}/documents/${personId}`,
  );
}

/**
 * PATCH /api/v1/documents/update-document
 *
 * Soft-delete (is_active = false) a supporting document attachment.
 * Used by the "remove" control on the Documents section of a person's profile.
 */
export function removeSupportingDocument(
  attachmentId: string,
  hospitalId: string,
): Promise<AxiosResponse<PatchSupportingDocumentResponse>> {
  return api.patch<PatchSupportingDocumentResponse>(
    `${API_PREFIX}/documents/update-document`,
    {
      doc_type: 'supporting_document',
      attachment_id: attachmentId,
      hospital_id: hospitalId,
    },
  );
}

/**
 * PATCH /api/v1/documents/update-document
 *
 * Soft-delete (is_active = false) a health record (serology/screening) attachment.
 * The backend's `health_record` doc_type route activates `attachment_id` and then
 * deactivates every id in `remove_attachment_ids` -- passing the same id in both
 * fields yields a net removal of that single attachment.
 * Used by the "remove" control on the Serology & Testing / Health Screening
 * attachment dropdown.
 */
export function removeHealthRecordAttachment(
  healthRecordId: string,
  attachmentId: string,
  hospitalId: string,
): Promise<AxiosResponse<PatchHealthRecordAttachmentResponse>> {
  return api.patch<PatchHealthRecordAttachmentResponse>(
    `${API_PREFIX}/documents/update-document`,
    {
      doc_type: 'health_record',
      health_record_id: healthRecordId,
      attachment_id: attachmentId,
      remove_attachment_ids: [attachmentId],
      hospital_id: hospitalId,
    },
  );
}

// ─── Milk Bottle Labels ──────────────────────────────────────────────────────

/**
 * POST /api/v1/milkBottleLabels
 *
 * Generate unique bottle-label IDs for a milk provider (mother or donor).
 * Returns an array of label objects whose `custom_label` field is the
 * human-readable bottle ID shown on printed collection labels.
 *
 * @param payload - provider_id (mother/donor person_id), milk_provider_type,
 *                  no_of_bottles, tenant_id
 */
export function generateMilkBottleLabels(
  payload: CreateMilkBottleLabelsRequest,
): Promise<AxiosResponse<CreateMilkBottleLabelsResponse>> {
  return api.post<CreateMilkBottleLabelsResponse>(
    `${API_PREFIX}/milkBottleLabels`,
    payload,
  );
}

// ─── Donor Dashboard ─────────────────────────────────────────────────────────

/**
 * GET /api/v1/donor/dashboard/:personId
 *
 * Returns aggregated donation stats for a donor:
 * total milk receivable count, total volume, volume used, babies helped,
 * and active days since approval.
 */
export function fetchDonorDashboard(
  personId: string,
): Promise<AxiosResponse<DonorDashboard>> {
  return api.get<DonorDashboard>(`${API_PREFIX}/donor/dashboard/${personId}`);
}
